import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';
import Layout from './Layout';
import { useTranslation } from 'react-i18next';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orderStats, setOrderStats] = useState({});
  const [driverStats, setDriverStats] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [exchangeRateInput, setExchangeRateInput] = useState('89000');

  const load = useCallback(async () => {
    const [oStats, dStats, orders, profile] = await Promise.all([
      API.get('orders/dashboard-stats/').catch(() => ({ data: {} })),
      API.get('drivers/dashboard-stats/').catch(() => ({ data: {} })),
      API.get('orders/').catch(() => ({ data: [] })),
      API.get('profile/me/').catch(() => ({ data: {} })),
    ]);

    setOrderStats(oStats.data || {});
    setDriverStats(dStats.data || {});
    setRecentOrders(Array.isArray(orders.data) ? orders.data.slice(0, 8) : []);
    setExchangeRateInput(String(profile?.data?.exchange_rate_usd_to_lbp || '89000'));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 25000);
    return () => clearInterval(timer);
  }, [load]);

  const saveExchangeRate = async () => {
    await API.post('profile/me/', { exchange_rate_usd_to_lbp: exchangeRateInput }).catch((err) => {
      const msg = err?.response?.data?.error || t('Save failed');
      window.alert(msg);
    });
    load();
  };

  const badge = (status) => {
    const mapped = t(`status.${status}`);
    return <span className={`badge ${status}`}>{mapped === `status.${status}` ? t(status) : mapped}</span>;
  };

  return (
    <Layout title={t('Dashboard')}>
      <div className="stats-grid">
        <div className="stat-card" onClick={() => navigate('/orders')}>
          <h4>{t('Total Orders')}</h4>
          <p>{orderStats.total || 0}</p>
        </div>
        <div className="stat-card warning" onClick={() => navigate('/orders')}>
          <h4>{t('Pending')}</h4>
          <p>{orderStats.pending || 0}</p>
        </div>
        <div className="stat-card info" onClick={() => navigate('/orders')}>
          <h4>{t('Offered')}</h4>
          <p>{orderStats.offered || 0}</p>
        </div>
        <div className="stat-card success" onClick={() => navigate('/orders')}>
          <h4>{t('Delivered')}</h4>
          <p>{orderStats.delivered || 0}</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card success" onClick={() => navigate('/drivers')}>
          <h4>{t('Drivers Online')}</h4>
          <p>{driverStats.online || 0}</p>
        </div>
        <div className="stat-card warning" onClick={() => navigate('/drivers')}>
          <h4>{t('Drivers Busy')}</h4>
          <p>{driverStats.busy || 0}</p>
        </div>
        <div className="stat-card" onClick={() => navigate('/drivers')}>
          <h4>{t('Drivers Offline')}</h4>
          <p>{driverStats.offline || 0}</p>
        </div>
        <div className="stat-card info" onClick={() => navigate('/drivers')}>
          <h4>{t('Total Drivers')}</h4>
          <p>{driverStats.total || 0}</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row-between" style={{ marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>{t('Exchange Rate')}</h3>
        </div>
        <div className="filters-row">
          <input
            className="input"
            type="number"
            min="1"
            step="0.01"
            value={exchangeRateInput}
            onChange={(e) => setExchangeRateInput(e.target.value)}
            placeholder={t('USD to LBP exchange rate')}
          />
          <button className="btn btn-secondary" type="button" onClick={saveExchangeRate}>{t('Save exchange rate')}</button>
        </div>
      </div>

      <div className="card">
        <div className="row-between" style={{ marginBottom: 12 }}>
          <h3>{t('Latest Orders')}</h3>
          <button className="btn btn-secondary" onClick={() => navigate('/orders')} type="button">
            {t('View All')}
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th>
                <th>{t('Customer')}</th>
                <th>{t('Recipient')}</th>
                <th>{t('Driver')}</th>
                <th>{t('Status')}</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((o) => (
                <tr key={o.id}>
                  <td>{o.order_number}</td>
                  <td>{o.customer_name}</td>
                  <td>{o.recipient_name || '-'}</td>
                  <td>{o.driver_name || '-'}</td>
                  <td>{badge(o.status)}</td>
                </tr>
              ))}
              {recentOrders.length === 0 && (
                <tr>
                  <td colSpan={5}>{t('No orders found')}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
